/**
 * AppSignal's setup.
 * https://docs.appsignal.com/front-end
 * (esp. https://docs.appsignal.com/front-end/hooks.html
 * for the decorators allowing to customize the data sent away)
 * @module lib/appsignal
 */

import Appsignal from '@appsignal/javascript';
import { plugin as windowEvents } from '@appsignal/plugin-window-events';
import { plugin as pathDecorator } from '@appsignal/plugin-path-decorator';
import { installTurboFrameMissingListener } from './appsignal/turbo_frame_missing_listener';
import { installUnhandledPromiseRejectionListener } from './appsignal/unhandled_promise_rejection_listener';

export function setupAppSignal({ key, revision }) {
  let appsignal = null;

  if (key) {
    appsignal = new Appsignal({
      key,
      revision,
    });

    // Capture uncaught Error and Promise rejections
    appsignal.use(windowEvents());

    // Helpful to know where things broke
    appsignal.use(pathDecorator());
    appsignal.addDecorator(urlParamsDecorator);

    // Fix AppSignal's grouping of errors
    appsignal.addDecorator(errorGroupingDecorator);

    appsignal.addDecorator(taggingDecorator);

    appsignal.addDecorator(browserInfoDecorator);

    installUnhandledPromiseRejectionListener(appsignal);

    window.appsignal = appsignal;
  }

  // Always install the Turbo Frame missing listener for error display
  // (even without AppSignal, we want to show user-friendly error messages)
  installTurboFrameMissingListener(appsignal);
}

/**
 * AppSignal's path decorator only adds the `path`
 * but the query params and hash can also be handy
 * for debugging
 */
function urlParamsDecorator(span) {
  if (!span) return span;

  return span.setTags({
    query_params: window.location.search,
    hash: window.location.hash,
  });
}

/**
 * AppSignal groups issues by `name`, but JS Errors
 * are not as typed as Ruby's so they'll mostly end
 * up with an "Error" name. Instead, the actual message
 * will get us clearer grouping
 */
function errorGroupingDecorator(span) {
  if (!span) return span;

  const serialized = span.serialize?.();
  const error = serialized?.error;

  if (!error) return span;

  // Skip errors that have no information
  const message = error.message || '';
  const stack =
    error.stack ||
    (Array.isArray(error.backtrace) ? error.backtrace.join('\n') : '') ||
    '';

  const hasNoInformation =
    message === '{}' && (stack === '' || stack === 'No stacktrace available');

  if (hasNoInformation) return span;

  const name = error.name || 'Error';
  const fullName = message ? `${name}: ${message}` : name;

  return span.setError({
    name: fullName,
    message: error.message,
    // Destructuring the error leads to missing stacktrace
    // as it seems the `error` object expects a `stack` property
    // as a String, unlike what's described in the docs
    // https://docs.appsignal.com/front-end/span.html#span-seterror-error-error-object
    stack: stack,
  });
}

/**
 * Extra browser info useful for debugging
 * (web or mobile platform, viewport size)
 * User agent should be already captured
 * by AppSignal
 */
function browserInfoDecorator(span) {
  if (!span) return span;

  return span.setTags({
    platform: platform(),
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  });
}

function platform() {
  if (window.webkit?.messageHandlers?.nativeApp) {
    return 'iOS';
  }
  if (window.nativeApp) {
    return 'Android';
  }
  return 'web';
}

/**
 * Adds custom tags from the metadata
 */
function taggingDecorator(span) {
  if (!span) return span;

  return span.setTags(getPageTags());
}

function getPageTags() {
  const tags = {};

  for (const element of document.head.querySelectorAll(
    "meta[name^='appsignal:tags']"
  )) {
    const key = element.name.replace('appsignal:tags:', '');
    tags[key] = element.content;
  }

  return tags;
}
